import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { requestApi } from "../../../actions/api";
import { GetLanguageString } from "../../helper/Components";
import {
  customMultiValue,
  customOption,
  LoadingSpinner,
} from "../../../constants/const";
import { ApiBooks } from "./ApiBooks";
import Draggable from "react-draggable";
import ApiSyllabusBook from "../setupsyllabus/ApiSetupBook";
import Select from "react-select";
import { GetSelectedLanguage } from "../../helper/Method";
import swal from "sweetalert";

const EditBooks = ({ editValue, setEditModal, setBooks }) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const [booksValues, setBooksValues] = useState({
    name: "",
    staticBookType: {},
  });

  const [syllabusBooks, setSyllabusBook] = useState([]);
  const [selectedStaticBookType, setSelectedStaticBookType] = useState({});
  const [errors, setErrors] = useState({});
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const toggle = () => setEditModal(false);

  const handleDrag = (e, ui) => {
    setPosition((prev) => ({
      x: prev.x + ui.deltaX,
      y: prev.y + ui.deltaY,
    }));
  };

  useEffect(() => {
    setLoading(true);

    requestApi("books/")
      .fetchById(editValue)
      .then((res) => {
        setBooksValues(res.data);
        setSelectedStaticBookType(res.data.staticBookType);
      })
      .catch(console.log);

    ApiSyllabusBook()
      .GetBooksBySyllabus({ Id: editValue })
      .then((res) => {
        const normalized = (res.data || []).map((o) => ({
          value: o.value,
          label: o.label,
          isSelected: o.isSelected,
          isOptional: o.isOptional,
        }));

        setSyllabusBook(normalized);
      })
      .catch(console.log)
      .finally(() => setLoading(false));
  }, [editValue]);

  const validate = () => {
    let temp = {};
    temp.name = booksValues.name ? "" : t("settings_name_error_message");
    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBooksValues((prev) => ({ ...prev, [name]: value }));
    setErrors({});
  };

  // =========================
  // SAFE SETS (important fix)
  // =========================
  const compulsorySet = new Set(
    syllabusBooks
      .filter((b) => b.isSelected && !b.isOptional)
      .map((b) => b.value),
  );

  const optionalSet = new Set(
    syllabusBooks
      .filter((b) => b.isSelected && b.isOptional)
      .map((b) => b.value),
  );

  const compulsoryValue = syllabusBooks.filter(
    (b) => b.isSelected && !b.isOptional,
  );

  const optionalValue = syllabusBooks.filter(
    (b) => b.isSelected && b.isOptional,
  );

  // =========================
  // COMPULSORY CHANGE
  // =========================
  const handleCompulsoryChange = (selected) => {
    const selectedIds = new Set(selected?.map((x) => x.value) || []);

    const updated = syllabusBooks.map((b) => {
      if (selectedIds.has(b.value)) {
        return {
          ...b,
          isSelected: true,
          isOptional: false,
        };
      }

      // preserve optional selection
      if (optionalSet.has(b.value)) return b;

      return {
        ...b,
        isSelected: false,
        isOptional: false,
      };
    });

    setSyllabusBook(updated);
  };

  // =========================
  // OPTIONAL CHANGE
  // =========================
  const handleOptionalChange = (selected) => {
    const selectedIds = new Set(selected?.map((x) => x.value) || []);

    const updated = syllabusBooks.map((b) => {
      if (selectedIds.has(b.value)) {
        return {
          ...b,
          isSelected: true,
          isOptional: true,
        };
      }

      // preserve compulsory selection
      if (compulsorySet.has(b.value)) return b;

      return {
        ...b,
        isSelected: false,
        isOptional: false,
      };
    });

    setSyllabusBook(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    const payload = {
      ...booksValues,
      staticBookType: selectedStaticBookType,
      selectedBooks: syllabusBooks
        .filter((b) => b.isSelected)
        .map((b) => ({
          value: b.value,
          isOptional: b.isOptional,
        })),
    };

    setLoading(true);

    ApiBooks()
      .Edit(payload)
      .then((res) => {
        setEditModal(false);

        requestApi("books/")
          .fetchAll()
          .then((res) => setBooks(res.data))
          .catch(console.log);

        swal({
          title:
            res.data.statusCode === "200"
              ? t("edited_setup_books")
              : t("common_error_message"),
          icon: res.data.statusCode === "200" ? "success" : "error",
          buttons: GetSelectedLanguage() === "en" ? "OK" : "ٹھیک ہے",
        });
      })
      .catch(console.log)
      .finally(() => setLoading(false));
  };

  return (
    <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
      <Modal isOpen={true} className="inmodal">
        <ModalHeader>
          <GetLanguageString props="settings_edit_books" />
        </ModalHeader>

        {!loading ? (
          <Form onSubmit={handleSubmit}>
            <ModalBody>
              {/* NAME */}
              <FormGroup>
                <Label>
                  <GetLanguageString props="common_name" />
                </Label>

                <Input
                  type="text"
                  name="name"
                  className="ur no-drag"
                  value={booksValues.name}
                  onChange={handleInputChange}
                  invalid={!!errors.name}
                />

                {errors.name && <FormFeedback>{errors.name}</FormFeedback>}
              </FormGroup>

              {/* COMPULSORY */}
              <FormGroup>
                <Label>
                  <GetLanguageString props="edit_syllabus_compulsory_books" />
                </Label>

                <Select
                  isMulti
                  options={syllabusBooks}
                  value={compulsoryValue}
                  components={{
                    Option: customOption,
                    MultiValue: customMultiValue,
                  }}
                  onChange={handleCompulsoryChange}
                  getOptionValue={(o) => o.value}
                  getOptionLabel={(o) => o.label}
                  closeMenuOnSelect={false}
                />
              </FormGroup>

              {/* OPTIONAL */}
              <FormGroup>
                <Label>
                  <GetLanguageString props="edit_syllabus_optional_books" />
                </Label>
                <Select
                  isMulti
                  options={syllabusBooks}
                  value={optionalValue}
                  components={{
                    Option: customOption,
                    MultiValue: customMultiValue,
                  }}
                  onChange={handleOptionalChange}
                  getOptionValue={(o) => o.value}
                  getOptionLabel={(o) => o.label}
                  closeMenuOnSelect={false}
                />
              </FormGroup>
            </ModalBody>

            <ModalFooter>
              <Button color="white" onClick={toggle}>
                <GetLanguageString props="common_cancel" />
              </Button>

              <Button color="primary" type="submit">
                <GetLanguageString props="common_save_changes" />
              </Button>
            </ModalFooter>
          </Form>
        ) : (
          LoadingSpinner()
        )}
      </Modal>
    </Draggable>
  );
};

export default EditBooks;
